import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { Edit, Save, X, Plus, Trash2, Package, GripVertical, Upload, Loader2 } from 'lucide-react';

const MAPLE_GRADES = [
  "Golden Color & Delicate Taste",
  "Amber Color & Rich Taste",
  "Dark Color & Robust Taste",
  "Very Dark Color & Strong Taste"
];

const ImageUploader = ({ images, onImagesChange, maxImages = 3 }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const currentImageCount = images.filter(img => img).length;
    if (currentImageCount + files.length > maxImages) {
      toast({
        title: "Too many images",
        description: `Maximum ${maxImages} images allowed`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      const newImages = [...images];
      
      for (const file of files) {
        // Check file size
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: "File too large",
            description: `${file.name} is larger than 5MB`,
            variant: "destructive",
          });
          continue;
        }

        // Upload file
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/uploads/image', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const data = await response.json();
        
        // Add to first empty slot
        const emptyIndex = newImages.findIndex(img => !img);
        if (emptyIndex !== -1) {
          newImages[emptyIndex] = data.url;
        } else if (newImages.length < maxImages) {
          newImages.push(data.url);
        }
      }

      onImagesChange(newImages);
      toast({
        title: "Upload complete",
        description: "Images uploaded successfully",
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: "Failed to upload images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index) => {
    const newImages = [...images];
    newImages[index] = '';
    onImagesChange(newImages);
  };

  const handleUrlChange = (index, value) => {
    const newImages = [...images];
    newImages[index] = value;
    onImagesChange(newImages);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="flex items-center">
          <Upload className="h-4 w-4 mr-2" />
          Product Images (1-{maxImages} photos)
        </Label>
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            multiple
            className="hidden"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || images.filter(img => img).length >= maxImages}
            variant="outline"
            className="border-2"
            style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload from Device
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((index) => (
          <div key={index} className="space-y-2">
            <div className="relative">
              {images[index] ? (
                <div className="relative">
                  <img
                    src={images[index]}
                    alt={`Product ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border-2"
                    style={{ borderColor: '#e5d5c0' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  className="w-full h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: '#e5d5c0' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="text-center">
                    <Upload className="h-8 w-8 mx-auto mb-1" style={{ color: '#736c64' }} />
                    <p className="text-xs" style={{ color: '#736c64' }}>
                      {index === 0 ? 'Main image' : 'Optional'}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <Input
              value={images[index] || ''}
              onChange={(e) => handleUrlChange(index, e.target.value)}
              placeholder={`Or paste URL ${index + 1}`}
              className="text-xs"
            />
          </div>
        ))}
      </div>
      <p className="text-xs" style={{ color: '#736c64' }}>
        Click to upload from your device, or paste image URLs directly
      </p>
    </div>
  );
};

const InventoryModeToggle = ({ useVariants, onToggle }) => {
  return (
    <div className="flex items-center space-x-4 p-4 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
      <Label className="font-semibold" style={{ color: '#4a2c1a' }}>Inventory Type:</Label>
      <div className="flex space-x-2">
        <Button
          type="button"
          variant={!useVariants ? "default" : "outline"}
          onClick={() => onToggle(false)}
          className={!useVariants ? "text-white" : ""}
          style={!useVariants ? { backgroundColor: '#FF8C19' } : {}}
          size="sm"
        >
          Simple (Single Quantity)
        </Button>
        <Button
          type="button"
          variant={useVariants ? "default" : "outline"}
          onClick={() => onToggle(true)}
          className={useVariants ? "text-white" : ""}
          style={useVariants ? { backgroundColor: '#FF8C19' } : {}}
          size="sm"
        >
          Maple Grades (4 Variants)
        </Button>
      </div>
    </div>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    size: '',
    price: '',
    description: '',
    images: ['', '', ''],
    useVariants: true,
    variants: MAPLE_GRADES.map(grade => ({ grade, inventory: 0 })),
    inventory: 0,
    sortOrder: 0,
    weight: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/products', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      data.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setProducts(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load products.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (product) => {
    setEditingId(product.id);
    const images = product.images || [product.image || ''];
    while (images.length < 3) images.push('');
    
    // Determine if product uses variants or simple inventory
    const hasVariants = product.variants && product.variants.length > 0 && 
      product.variants.some(v => v.inventory > 0 || product.inventory === 0);
    
    const variants = product.variants && product.variants.length > 0
      ? product.variants
      : MAPLE_GRADES.map(grade => ({ grade, inventory: 0 }));
    
    setEditForm({
      name: product.name,
      size: product.size,
      price: product.price,
      description: product.description,
      images: images,
      useVariants: hasVariants,
      variants: variants,
      inventory: product.inventory || 0,
      sortOrder: product.sortOrder || 0,
      weight: product.weight || 0
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (productId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const filteredImages = editForm.images.filter(img => img.trim() !== '');
      
      // Prepare data based on inventory mode
      const updateData = {
        name: editForm.name,
        size: editForm.size,
        price: editForm.price,
        description: editForm.description,
        images: filteredImages.length > 0 ? filteredImages : undefined,
        sortOrder: editForm.sortOrder,
        weight: parseFloat(editForm.weight) || 0
      };
      
      if (editForm.useVariants) {
        // Using maple grade variants
        updateData.variants = editForm.variants;
        // Total inventory calculated from variants
      } else {
        // Using simple inventory
        updateData.variants = [];
        updateData.inventory = parseInt(editForm.inventory) || 0;
      }
      
      const response = await fetch(`/api/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) throw new Error('Failed to update');

      toast({
        title: "Success!",
        description: "Product updated successfully.",
      });
      
      setEditingId(null);
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update product.",
        variant: "destructive",
      });
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      const filteredImages = newProduct.images.filter(img => img.trim() !== '');
      
      const productData = {
        name: newProduct.name,
        size: newProduct.size,
        price: parseFloat(newProduct.price),
        description: newProduct.description,
        images: filteredImages,
        sortOrder: products.length,
        weight: parseFloat(newProduct.weight) || 0
      };
      
      if (newProduct.useVariants) {
        productData.variants = newProduct.variants;
      } else {
        productData.variants = [];
        productData.inventory = parseInt(newProduct.inventory) || 0;
      }
      
      const response = await fetch('/api/products/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) throw new Error('Failed to create product');

      toast({
        title: "Success!",
        description: "Product created successfully.",
      });
      
      setShowAddForm(false);
      setNewProduct({
        name: '',
        size: '',
        price: '',
        description: '',
        images: ['', '', ''],
        useVariants: true,
        variants: MAPLE_GRADES.map(grade => ({ grade, inventory: 0 })),
        inventory: 0,
        sortOrder: 0,
        weight: ''
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create product.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast({
        title: "Success!",
        description: "Product deleted successfully.",
      });
      
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete product.",
        variant: "destructive",
      });
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newProducts = [...products];
    const draggedProduct = newProducts[draggedItem];
    newProducts.splice(draggedItem, 1);
    newProducts.splice(index, 0, draggedProduct);
    
    setProducts(newProducts);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const productOrders = products.map((product, index) => ({
        id: product.id,
        sortOrder: index
      }));
      
      await fetch('/api/products/reorder', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productOrders),
      });
      
      toast({
        title: "Success!",
        description: "Product order saved.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save product order.",
        variant: "destructive",
      });
    }
    
    setDraggedItem(null);
  };

  const updateVariantInventory = (formSetter, variants, gradeIndex, newInventory) => {
    const updatedVariants = [...variants];
    updatedVariants[gradeIndex] = {
      ...updatedVariants[gradeIndex],
      inventory: parseInt(newInventory) || 0
    };
    formSetter(prev => ({ ...prev, variants: updatedVariants }));
  };

  const getTotalInventory = (variants) => {
    return variants.reduce((sum, v) => sum + (v.inventory || 0), 0);
  };

  if (loading) {
    return <div className="text-center py-8">Loading products...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: '#4a2c1a' }}>
            Product Management
          </h2>
          <p className="text-sm mt-1" style={{ color: '#736c64' }}>
            Drag products to reorder them on the shop page
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="text-white font-semibold"
          style={{ backgroundColor: '#FF8C19' }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Product
        </Button>
      </div>

      {/* Add New Product Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border-2" style={{ borderColor: '#FF8C19' }}>
          <h3 className="text-xl font-bold mb-4" style={{ color: '#4a2c1a' }}>
            Add New Product
          </h3>
          <form onSubmit={handleAddProduct} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Product Name *</Label>
                <Input
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  placeholder="e.g., 500ml Bottle"
                  required
                />
              </div>
              <div>
                <Label>Size *</Label>
                <Input
                  value={newProduct.size}
                  onChange={(e) => setNewProduct({...newProduct, size: e.target.value})}
                  placeholder="e.g., 500ml"
                  required
                />
              </div>
              <div>
                <Label>Price ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  placeholder="e.g., 35.00"
                  required
                />
              </div>
              <div>
                <Label>Weight (lbs)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newProduct.weight}
                  onChange={(e) => setNewProduct({...newProduct, weight: e.target.value})}
                  placeholder="e.g., 1.53"
                />
              </div>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Input
                value={newProduct.description}
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                placeholder="Product description..."
                required
              />
            </div>

            {/* Image Uploader */}
            <ImageUploader
              images={newProduct.images}
              onImagesChange={(images) => setNewProduct({...newProduct, images})}
            />

            {/* Inventory Mode Toggle */}
            <InventoryModeToggle
              useVariants={newProduct.useVariants}
              onToggle={(useVariants) => setNewProduct({...newProduct, useVariants})}
            />

            {/* Maple Grade Variants or Simple Inventory */}
            {newProduct.useVariants ? (
              <div>
                <Label className="mb-2 block">Maple Grade Inventory</Label>
                <div className="grid grid-cols-2 gap-4">
                  {newProduct.variants.map((variant, index) => (
                    <div key={variant.grade} className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
                      <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>{variant.grade}</p>
                      </div>
                      <div className="w-24">
                        <Input
                          type="number"
                          min="0"
                          value={variant.inventory}
                          onChange={(e) => updateVariantInventory(setNewProduct, newProduct.variants, index, e.target.value)}
                          placeholder="Qty"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm mt-2" style={{ color: '#736c64' }}>
                  Total Inventory: <strong>{getTotalInventory(newProduct.variants)}</strong> units
                </p>
              </div>
            ) : (
              <div>
                <Label className="mb-2 block">Inventory Quantity</Label>
                <div className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>Total Units Available</p>
                    <p className="text-xs" style={{ color: '#736c64' }}>For products without maple grade options (e.g., bundles, gift sets)</p>
                  </div>
                  <div className="w-32">
                    <Input
                      type="number"
                      min="0"
                      value={newProduct.inventory}
                      onChange={(e) => setNewProduct({...newProduct, inventory: parseInt(e.target.value) || 0})}
                      placeholder="Quantity"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              <Button
                type="submit"
                className="text-white"
                style={{ backgroundColor: '#22c55e' }}
              >
                <Save className="h-4 w-4 mr-2" />
                Create Product
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddForm(false)}
                variant="outline"
                className="border-2"
                style={{ borderColor: '#dc2626', color: '#dc2626' }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Products List */}
      <div className="space-y-4">
        {products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-lg">
            <Package className="h-16 w-16 mx-auto mb-4" style={{ color: '#736c64' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#4a2c1a' }}>No products yet</h3>
            <p style={{ color: '#736c64' }}>Click "Add New Product" to create your first product.</p>
          </div>
        ) : (
          products.map((product, index) => (
            <div 
              key={product.id} 
              className="bg-white rounded-lg shadow-lg p-6"
              draggable={editingId !== product.id}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              {editingId === product.id ? (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Product Name</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Size</Label>
                      <Input
                        value={editForm.size}
                        onChange={(e) => setEditForm({...editForm, size: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Weight (lbs)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.weight || ''}
                        onChange={(e) => setEditForm({...editForm, weight: parseFloat(e.target.value) || 0})}
                        placeholder="e.g., 1.53"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={editForm.description}
                      onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    />
                  </div>

                  {/* Image Uploader */}
                  <ImageUploader
                    images={editForm.images || ['', '', '']}
                    onImagesChange={(images) => setEditForm({...editForm, images})}
                  />

                  {/* Inventory Mode Toggle */}
                  <InventoryModeToggle
                    useVariants={editForm.useVariants}
                    onToggle={(useVariants) => setEditForm({...editForm, useVariants})}
                  />

                  {/* Maple Grade Variants or Simple Inventory */}
                  {editForm.useVariants ? (
                    <div>
                      <Label className="mb-2 block">Maple Grade Inventory</Label>
                      <div className="grid grid-cols-2 gap-4">
                        {(editForm.variants || []).map((variant, idx) => (
                          <div key={variant.grade} className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
                            <div className="flex-1">
                              <p className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>{variant.grade}</p>
                            </div>
                            <div className="w-24">
                              <Input
                                type="number"
                                min="0"
                                value={variant.inventory}
                                onChange={(e) => updateVariantInventory(setEditForm, editForm.variants, idx, e.target.value)}
                                placeholder="Qty"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm mt-2" style={{ color: '#736c64' }}>
                        Total Inventory: <strong>{getTotalInventory(editForm.variants || [])}</strong> units
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Label className="mb-2 block">Inventory Quantity</Label>
                      <div className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
                        <div className="flex-1">
                          <p className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>Total Units Available</p>
                          <p className="text-xs" style={{ color: '#736c64' }}>For products without maple grade options (e.g., bundles, gift sets)</p>
                        </div>
                        <div className="w-32">
                          <Input
                            type="number"
                            min="0"
                            value={editForm.inventory || 0}
                            onChange={(e) => setEditForm({...editForm, inventory: parseInt(e.target.value) || 0})}
                            placeholder="Quantity"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <Button
                      onClick={() => saveEdit(product.id)}
                      className="text-white"
                      style={{ backgroundColor: '#22c55e' }}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button
                      onClick={cancelEdit}
                      variant="outline"
                      className="border-2"
                      style={{ borderColor: '#dc2626', color: '#dc2626' }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  {/* Drag Handle */}
                  <div className="mr-4 cursor-grab active:cursor-grabbing" style={{ color: '#736c64' }}>
                    <GripVertical className="h-6 w-6" />
                  </div>
                  
                  {/* Product Info */}
                  <div className="flex-1 flex items-start space-x-4">
                    <div className="flex space-x-2">
                      {(product.images && product.images.length > 0 ? product.images : [product.image]).slice(0, 3).map((img, idx) => (
                        img && <img key={idx} src={img} alt={`${product.name} ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                      ))}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold" style={{ color: '#4a2c1a' }}>{product.name}</h3>
                      <p className="text-sm" style={{ color: '#736c64' }}>{product.size}</p>
                      <p className="text-2xl font-bold mt-2" style={{ color: '#FF8C19' }}>${product.price}</p>
                      <p className="text-sm mt-2" style={{ color: '#736c64' }}>{product.description}</p>
                      
                      {/* Variant Inventory Summary */}
                      {product.variants && product.variants.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {product.variants.map((variant) => (
                            <span 
                              key={variant.grade}
                              className="px-2 py-1 rounded text-xs font-semibold"
                              style={{ 
                                backgroundColor: variant.inventory > 0 ? '#dcfce7' : '#fee2e2',
                                color: variant.inventory > 0 ? '#166534' : '#dc2626'
                              }}
                            >
                              {variant.grade.split(' & ')[0]}: {variant.inventory}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div className="mt-3 flex items-center space-x-4">
                        {/* Total Inventory Badge */}
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>
                            Total:
                          </span>
                          <span 
                            className="px-3 py-1 rounded-full text-sm font-semibold"
                            style={{ 
                              backgroundColor: (product.inventory || 0) > 10 ? '#dcfce7' : (product.inventory || 0) > 0 ? '#fef3c7' : '#fee2e2',
                              color: (product.inventory || 0) > 10 ? '#166534' : (product.inventory || 0) > 0 ? '#92400e' : '#dc2626'
                            }}
                          >
                            {product.inventory || 0} units
                          </span>
                        </div>
                        {/* Stock Status Badge */}
                        {product.inStock ? (
                          <span className="px-3 py-1 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: '#22c55e' }}>
                            In Stock
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: '#dc2626' }}>
                            Out of Stock
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex space-x-2 ml-4">
                    <Button
                      onClick={() => startEdit(product)}
                      className="text-white"
                      style={{ backgroundColor: '#FF8C19' }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteProduct(product.id)}
                      variant="outline"
                      className="border-2"
                      style={{ borderColor: '#dc2626', color: '#dc2626' }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductsTab;
